import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Appointment, BusinessRating } from '../types';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import CustomerCancellationModal from '../components/CustomerCancellationModal';
import ServiceReviewModal from '../components/ServiceReviewModal';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import { getAppointmentStartIso, getTotalDuration, hasCustomTimeBlocks, isPendingDepositOutstanding } from '../utils/appointmentHelpers';
import { RebookConfirmModal } from '../components/RebookConfirmModal';
import { buildRebookBookingUrl } from '../utils/rebookUtils';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Building2,
  User,
  DollarSign,
  MapPin,
  Phone,
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
  Trash2,
  Star,
  CalendarPlus,
  Navigation,
  ChevronDown,
  Settings,
  Heart,
  Info,
  MessageSquare,
  Repeat,
  ListOrdered,
  FileText,
  HelpCircle
} from 'lucide-react';

const CustomerAppointmentDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { user, logout } = useAuth();

  const cameFromInvoices = (location.state as any)?.from === '/dashboard/customer/invoices';
  const backTarget = cameFromInvoices ? '/dashboard/customer/invoices' : '/dashboard/customer/appointments';
  const backLabel = cameFromInvoices ? 'Back to Invoices' : 'Back to Appointments';
  
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [businessInfo, setBusinessInfo] = useState<any>(null);
  const [customerAppointmentMessage, setCustomerAppointmentMessage] = useState<{ enabled: boolean; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Cancellation modal state
  const [cancellationAppointment, setCancellationAppointment] = useState<Appointment | null>(null);
  const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Review modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [appointmentReviews, setAppointmentReviews] = useState<any[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [businessRating, setBusinessRating] = useState<BusinessRating | null>(null);

  // Rebook modal state
  const [isRebookModalOpen, setIsRebookModalOpen] = useState(false);
  
  // Manage dropdown state
  const [isManageDropdownOpen, setIsManageDropdownOpen] = useState(false);
  const manageDropdownRef = useRef<HTMLDivElement>(null);
  
  // Calendar dropdown state
  const [isCalendarDropdownOpen, setIsCalendarDropdownOpen] = useState(false);
  const calendarDropdownRef = useRef<HTMLDivElement>(null);
  
  // Notifications state for navbar
  const [unreadNotifications, setUnreadNotifications] = useState<any[]>([]);
  const [seriesDepositDue, setSeriesDepositDue] = useState(false);

  useEffect(() => {
    const loadAppointment = async () => {
      if (!appointmentId) {
        setError('Appointment ID is required');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      setCustomerAppointmentMessage(null);
      
      try {
        const appointmentData = await apiService.getAppointment(appointmentId);
        
        // Verify this appointment belongs to the current user
        if (appointmentData.customerId !== user?.id) {
          setError('You do not have permission to view this appointment');
          setLoading(false);
          return;
        }
        
        setAppointment(appointmentData);

        if (appointmentData.status === 'PENDING') {
          try {
            const depositInfo = await apiService.getAppointmentDeposit(appointmentId);
            setSeriesDepositDue(!depositInfo.alreadyPaid && depositInfo.amountDueCents > 0);
          } catch {
            setSeriesDepositDue(isPendingDepositOutstanding(appointmentData));
          }
        } else {
          setSeriesDepositDue(false);
        }
        
        // Load business info
        if (appointmentData.business_short_name) {
          try {
            const business = await apiService.getBusiness(appointmentData.business_short_name);
            setBusinessInfo(business?.business || business);
          } catch (error) {
            console.error('Error loading business info:', error);
          }

          // Load optional customer appointment message (business settings)
          try {
            const msg = await apiService.getCustomerAppointmentMessageByShortName(appointmentData.business_short_name);
            setCustomerAppointmentMessage(msg);
          } catch (error) {
            console.error('Error loading customer appointment message:', error);
          }
        }
        
        // Load reviews
        try {
          setReviewsLoading(true);
          const reviews = await apiService.getAppointmentReviews(appointmentId);
          setAppointmentReviews(reviews || []);
        } catch (error) {
          console.error('Error loading reviews:', error);
        } finally {
          setReviewsLoading(false);
        }
        
        // Load business rating
        try {
          const rating = await apiService.getBusinessRatingForAppointment(appointmentId);
          setBusinessRating(rating);
        } catch (error) {
          console.error('Error loading business rating:', error);
          setBusinessRating(null);
        }
      } catch (error: any) {
        console.error('Error loading appointment:', error);
        setError(error.response?.data?.message || 'Failed to load appointment details');
      } finally {
        setLoading(false);
      }
    };
    
    loadAppointment();
  }, [appointmentId, user?.id]);

  // Load notifications for navbar
  useEffect(() => {
    const loadNotifications = async () => {
      if (!user?.id) return;
      
      try {
        const response = await apiService.getNotifications();
        const notifications = response.data || [];
        const unread = notifications.filter((n: any) => !n.is_read);
        setUnreadNotifications(unread);
      } catch (error) {
        console.error('Error loading notifications:', error);
      }
    };
    
    loadNotifications();
  }, [user?.id]);

  // Close manage dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (manageDropdownRef.current && !manageDropdownRef.current.contains(event.target as Node)) {
        setIsManageDropdownOpen(false);
      }
    };

    if (isManageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isManageDropdownOpen]);

  // Close calendar dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarDropdownRef.current && !calendarDropdownRef.current.contains(event.target as Node)) {
        setIsCalendarDropdownOpen(false);
      }
    };

    if (isCalendarDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarDropdownOpen]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  /** Time string: single time or "Between x – y" when flex-only (no firm scheduledAt) */
  const getAppointmentTimeDisplay = (appointment: any) => {
    if (appointment?.scheduledAt) {
      return formatTime(appointment.scheduledAt);
    }
    const flex = appointment?.flexScheduledAt;
    if (flex?.earliest && flex?.latest) {
      return `Between ${formatTime(flex.earliest)} – ${formatTime(flex.latest)}`;
    }
    const iso = getAppointmentStartIso(appointment);
    return iso ? formatTime(iso) : '—';
  };

  const getStatusColor = (status: string, appointment?: { status?: string; requiredDepositAmount?: number | string | null; required_deposit_amount?: number | string | null; receivedDepositAmount?: number | string | null; received_deposit_amount?: number | string | null; seriesId?: string | null; series_id?: string | null }) => {
    if (status === 'PENDING' && appointment != null) {
      if (isPendingDepositOutstanding(appointment) || seriesDepositDue) return 'bg-orange-100 text-orange-800';
    }
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'PENDING_QUOTE':
        return 'bg-orange-100 text-orange-800';
      case 'CONFIRMED':
        return 'bg-blue-50 text-blue-900';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status: string, appointment?: { status?: string; requiredDepositAmount?: number | string | null; required_deposit_amount?: number | string | null; receivedDepositAmount?: number | string | null; received_deposit_amount?: number | string | null; seriesId?: string | null; series_id?: string | null }) => {
    if (!status) return '';
    if (status === 'PENDING_QUOTE') return 'Pending Quote';
    if (status === 'PENDING' && appointment != null) {
      if (isPendingDepositOutstanding(appointment) || seriesDepositDue) return 'Pending Deposit';
    }
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const getStatusIcon = (status: string, appointment?: { status?: string; requiredDepositAmount?: number | string | null; required_deposit_amount?: number | string | null; receivedDepositAmount?: number | string | null; received_deposit_amount?: number | string | null; seriesId?: string | null; series_id?: string | null }) => {
    if (status === 'PENDING' && appointment != null) {
      if (isPendingDepositOutstanding(appointment) || seriesDepositDue) return { icon: Clock, color: 'bg-orange-100 text-orange-700' };
    }
    switch (status) {
      case 'CONFIRMED':
        return { icon: CheckCircle, color: 'bg-blue-50 text-blue-900' };
      case 'PENDING':
        return { icon: Clock, color: 'bg-yellow-100 text-yellow-600' };
      case 'PENDING_QUOTE':
        return { icon: AlertCircle, color: 'bg-orange-100 text-orange-700' };
      case 'COMPLETED':
        return { icon: CheckCircle, color: 'bg-green-100 text-green-600' };
      case 'CANCELLED':
        return { icon: XCircle, color: 'bg-red-100 text-red-600' };
      default:
        return { icon: Calendar, color: 'bg-gray-100 text-gray-600' };
    }
  };

  const calculateTotalDuration = () => getTotalDuration(appointment as any);
  const isCustomBlocks = hasCustomTimeBlocks(appointment as any);

  const calculateTotalPrice = () => {
    if (!appointment?.assignments) return 0;
    return appointment.assignments.reduce((total, assignment) => {
      let price = 0;
      if (typeof assignment.price === 'number') {
        price = assignment.price;
      } else if (typeof assignment.price === 'string') {
        price = parseFloat(assignment.price) || 0;
      }
      return total + price;
    }, 0);
  };

  const handleRescheduleAppointment = () => {
    if (appointment) {
      if (isCustomBlocks) {
        toast.error('This appointment has custom service timing. Please contact the business to reschedule.');
        return;
      }
      navigate(`/dashboard/customer/reschedule/${appointment.id}`);
    }
  };

  const handleCancelAppointment = () => {
    if (appointment) {
      setCancellationAppointment(appointment);
      setIsCancellationModalOpen(true);
    }
  };

  const handleCancellationConfirmed = async (cancellationData: { noShow: boolean; cancellationNotes: string; cancelScope?: 'THIS' | 'REMAINING' }) => {
    if (!cancellationAppointment) return;

    setIsCancelling(true);
    try {
      await apiService.cancelAppointment(cancellationAppointment.id, cancellationData);
      toast.success('We\'re sorry to see you cancel your appointment. Please feel free to schedule a new appointment anytime - we\'d love to have you back!', {
        duration: 6000,
        style: {
          background: '#764ba2',
          color: 'white',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '14px',
          lineHeight: '1.4'
        }
      });
      // Reload appointment to show updated status
      if (appointmentId) {
        const appointmentData = await apiService.getAppointment(appointmentId);
        setAppointment(appointmentData);
      }
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Failed to cancel appointment');
    } finally {
      setIsCancelling(false);
      setCancellationAppointment(null);
      setIsCancellationModalOpen(false);
    }
  };

  const handleCancellationCancelled = () => {
    setCancellationAppointment(null);
    setIsCancellationModalOpen(false);
  };

  const handleRateAppointment = () => {
    setIsReviewModalOpen(true);
  };

  const handleRebookAppointment = () => {
    if (!appointment) return;
    setIsRebookModalOpen(true);
  };

  const handleRebookContinue = () => {
    if (!appointment) return;
    const url = buildRebookBookingUrl(appointment, 2);
    setIsRebookModalOpen(false);
    if (url) navigate(url);
  };

  const handleRebookEditServices = () => {
    if (!appointment) return;
    const url = buildRebookBookingUrl(appointment, 1);
    setIsRebookModalOpen(false);
    if (url) navigate(url);
  };

  const handleReviewModalClose = () => {
    setIsReviewModalOpen(false);
  };

  const handleReviewSubmitted = async () => {
    // Reload reviews
    if (appointmentId) {
      try {
        setReviewsLoading(true);
        const reviews = await apiService.getAppointmentReviews(appointmentId);
        setAppointmentReviews(reviews || []);
      } catch (error) {
        console.error('Error loading reviews:', error);
      } finally {
        setReviewsLoading(false);
      }
      
      // Reload business rating
      try {
        const rating = await apiService.getBusinessRatingForAppointment(appointmentId);
        setBusinessRating(rating);
      } catch (error) {
        console.error('Error loading business rating:', error);
        setBusinessRating(null);
      }
    }
  };

  // Generate Google Calendar URL
  const getGoogleCalendarUrl = () => {
    if (!appointment) return '';
    
    const startIso = getAppointmentStartIso(appointment);
    if (!startIso) return '';
    const startDate = new Date(startIso);
    const totalDuration = calculateTotalDuration() || 60;
    const endDate = new Date(startDate.getTime() + totalDuration * 60000);
    
    const formatGoogleDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const serviceNames = appointment.assignments?.map(a => a.serviceName).join(', ') || 'Appointment';
    const description = `Appointment at ${appointment.business_name}\n\nServices: ${serviceNames}${appointment.notes ? `\n\nNotes: ${appointment.notes}` : ''}`;
    const location = appointment.business_name || '';
    
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: `${serviceNames} at ${appointment.business_name}`,
      dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
      details: description,
      location: location
    });
    
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  // Generate Outlook Calendar URL
  const getOutlookCalendarUrl = () => {
    if (!appointment) return '';
    
    const startIso = getAppointmentStartIso(appointment);
    if (!startIso) return '';
    const startDate = new Date(startIso);
    const totalDuration = calculateTotalDuration() || 60;
    const endDate = new Date(startDate.getTime() + totalDuration * 60000);
    
    const formatOutlookDate = (date: Date) => {
      return date.toISOString();
    };
    
    const serviceNames = appointment.assignments?.map(a => a.serviceName).join(', ') || 'Appointment';
    const description = `Appointment at ${appointment.business_name}\n\nServices: ${serviceNames}${appointment.notes ? `\n\nNotes: ${appointment.notes}` : ''}`;
    const location = appointment.business_name || '';
    
    const params = new URLSearchParams({
      subject: `${serviceNames} at ${appointment.business_name}`,
      startdt: formatOutlookDate(startDate),
      enddt: formatOutlookDate(endDate),
      body: description,
      location: location
    });
    
    return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
  };

  // Download ICS file
  const downloadICSFile = () => {
    if (!appointment) return;
    
    try {
      const startIso = getAppointmentStartIso(appointment);
      if (!startIso) return;
      const startDate = new Date(startIso);
      const totalDuration = calculateTotalDuration() || 60;
      const endDate = new Date(startDate.getTime() + totalDuration * 60000);
      
      const formatICSDate = (date: Date) => {
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        const hours = String(date.getUTCHours()).padStart(2, '0');
        const minutes = String(date.getUTCMinutes()).padStart(2, '0');
        const seconds = String(date.getUTCSeconds()).padStart(2, '0');
        return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
      };
      
      const serviceNames = appointment.assignments?.map(a => a.serviceName).join(', ') || 'Appointment';
      const description = `Appointment at ${appointment.business_name}\n\nServices: ${serviceNames}${appointment.notes ? `\n\nNotes: ${appointment.notes}` : ''}`;
      const location = appointment.business_name || '';
      
      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Appointment Booking System//EN',
        'BEGIN:VEVENT',
        `UID:${appointment.id}@appointment-booking`,
        `DTSTART:${formatICSDate(startDate)}`,
        `DTEND:${formatICSDate(endDate)}`,
        `SUMMARY:${serviceNames} at ${appointment.business_name}`,
        `DESCRIPTION:${description.replace(/\n/g, '\\n')}`,
        `LOCATION:${location}`,
        'STATUS:CONFIRMED',
        'SEQUENCE:0',
        'BEGIN:VALARM',
        'TRIGGER:-PT1H',
        'ACTION:DISPLAY',
        'DESCRIPTION:Reminder: Appointment in 1 hour',
        'END:VALARM',
        'END:VEVENT',
        'END:VCALENDAR'
      ].join('\r\n');
      
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `appointment-${appointment.id}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      toast.success('Calendar event downloaded. Open it to add to your calendar.');
    } catch (error) {
      console.error('Error generating calendar file:', error);
      toast.error('Failed to generate calendar file. Please try again.');
    }
  };

  const handleGetDirections = async () => {
    if (!appointment) return;
    
    try {
      let address = '';
      if (appointment.business_short_name) {
        try {
          const business = await apiService.getBusiness(appointment.business_short_name);
          address = business?.business?.address || business?.address || '';
        } catch (error) {
          console.error('Error fetching business info:', error);
        }
      }
      
      // Construct Google Maps URL - use address only, fallback to business name if no address
      const query = address 
        ? encodeURIComponent(address)
        : encodeURIComponent(appointment.business_name || '');
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${query}`;
      
      window.open(mapsUrl, '_blank');
    } catch (error) {
      console.error('Error getting directions:', error);
      toast.error('Failed to open directions. Please try again.');
    }
  };

  const hasAnyServicesReviewed = () => {
    return appointmentReviews.length > 0;
  };

  // Check if appointment has any reviews (service reviews) or business ratings
  const hasAnyReviewsOrRatings = () => {
    return appointmentReviews.length > 0 || (businessRating !== null && businessRating !== undefined);
  };

  const isAppointmentInPast = () => {
    if (!appointment) return false;
    const startIso = getAppointmentStartIso(appointment);
    if (!startIso) return false;
    const start = new Date(startIso);
    if (Number.isNaN(start.getTime())) return false;

    // Prefer explicit per-service end times when present (supports multi-professional + DST boundaries)
    const assignmentEndTimes = (appointment.assignments || [])
      .map(a => (a.toDateTime ? new Date(a.toDateTime) : null))
      .filter((d): d is Date => !!d && !Number.isNaN(d.getTime()));

    const end = assignmentEndTimes.length
      ? new Date(Math.max(...assignmentEndTimes.map(d => d.getTime())))
      : (() => {
          const totalMinutes = calculateTotalDuration() || 0;
          return totalMinutes > 0 ? new Date(start.getTime() + totalMinutes * 60000) : start;
        })();

    return end.getTime() < Date.now();
  };

  if (loading) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar 
          onLogout={logout}
          showDashboardLink={true}
          showLogoutButton={true}
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading appointment details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar 
          onLogout={logout}
          showDashboardLink={true}
          showLogoutButton={true}
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
        />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Appointment</h1>
            <p className="text-gray-600 mb-6">{error || 'Appointment not found'}</p>
            <button
              onClick={() => navigate(backTarget)}
              className="aacore-btn-primary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {backLabel}
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const { icon: StatusIcon, color: statusIconColor } = getStatusIcon(appointment.status, appointment);
  const hasViewableInvoice = appointment.invoiceId && appointment.invoiceStatus !== 'DRAFT' && appointment.invoiceStatus !== 'VOIDED';
  const totalDuration = calculateTotalDuration();
  const totalPrice = calculateTotalPrice();
  const isPendingQuote = appointment.status === 'PENDING_QUOTE';
  const pendingQuoteCardStyle = isPendingQuote
    ? ({
        backgroundColor: '#FFFEFD', // near-white warm tint
        backgroundImage:
          'repeating-linear-gradient(135deg, rgba(251, 146, 60, 0.02) 0px, rgba(251, 146, 60, 0.02) 6px, transparent 6px, transparent 18px), repeating-linear-gradient(225deg, rgba(251, 146, 60, 0.02) 0px, rgba(251, 146, 60, 0.02) 6px, transparent 6px, transparent 18px)',
        backgroundSize: '18px 18px',
        backgroundPosition: '0 0, 9px 9px'
      } as React.CSSProperties)
    : undefined;
  const showCustomerAppointmentMessage =
    Boolean(customerAppointmentMessage?.enabled) &&
    Boolean(customerAppointmentMessage?.message && customerAppointmentMessage.message.trim());

  return (
    <div className="min-h-screen no-animations flex flex-col relative">
      <CustomerNavbar 
        onLogout={logout}
        showDashboardLink={true}
        showLogoutButton={true}
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
      />
      
      <div className="flex-1 pt-6 pb-12 overflow-visible">
        <div className="px-4 lg:px-8 max-w-7xl mx-auto overflow-visible">
          {/* Breadcrumb Navigation */}
          <div className="mb-4">
            <button
              onClick={() => navigate(backTarget)}
              className="flex items-center font-semibold"
              style={{ color: '#0B4778' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#0a3d66'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#0B4778'}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {backLabel}
            </button>
          </div>

          {/* Page Header */}
          <div className="mb-4">
            <h1 className="text-xl font-bold mb-0">Appointment Details</h1>
            <p className="text-gray-600 text-sm mb-0">View & manage your appointment</p>
          </div>

          {/* Appointment Card */}
          <div
            style={pendingQuoteCardStyle}
            className={`bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-visible ${
              isPendingQuote ? 'border-orange-200' : 'border-gray-200'
            }`}
          >
            <div className="p-6 overflow-visible">
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0 relative">
                  <div className="absolute top-0 right-0 flex items-center gap-1.5">
                    {hasViewableInvoice && (
                      <Link
                        to={`/dashboard/customer/invoices/${appointment.invoiceId}`}
                        className="inline-flex items-center justify-center w-8 h-6 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors"
                        title="View invoice"
                        aria-label="View invoice"
                      >
                        <FileText className="w-4 h-4" />
                      </Link>
                    )}
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full flex-shrink-0 ${getStatusColor(appointment.status, appointment)}`}>
                      {formatStatus(appointment.status, appointment)}
                    </span>
                  </div>
                  <div className={`mb-4 ${hasViewableInvoice ? 'pr-24' : 'pr-20'}`}>
                    <div className="mb-3">
                      <div className="text-lg font-bold text-gray-900">
                        {getAppointmentStartIso(appointment) ? formatDate(getAppointmentStartIso(appointment)!) : 'Date TBD'}
                      </div>
                      <div className="text-sm font-semibold flex items-center mt-1" style={{ color: '#0B4778' }}>
                        <Clock className="w-4 h-4 mr-1" />
                        {getAppointmentTimeDisplay(appointment)}
                        {(!!(appointment as any)?.scheduledAt ||
                          !((appointment as any)?.flexScheduledAt?.earliest && (appointment as any)?.flexScheduledAt?.latest)) &&
                          totalDuration > 0 &&
                          (() => {
                            const startIso = getAppointmentStartIso(appointment);
                            const startTime = startIso ? new Date(startIso) : null;
                            if (!startTime) return null;
                            const endTime = new Date(startTime.getTime() + totalDuration * 60000);
                            return (
                              <span>
                                {'\u00A0-\u00A0'}
                                {endTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                              </span>
                            );
                          })()}
                        {Boolean((appointment as any)?.seriesId || (appointment as any)?.series_id) ? (
                          <span
                            className="inline-flex items-center gap-1 ml-2 text-xs font-bold px-2 py-0.5 rounded-full"
                            aria-label="Recurring appointment"
                            title="Recurring appointment"
                            style={{ color: '#0B4778', backgroundColor: 'rgba(11, 71, 120, 0.10)' }}
                          >
                            <Repeat className="w-3.5 h-3.5" aria-hidden style={{ color: '#0B4778' }} />
                            <span>Recurring</span>
                          </span>
                        ) : null}
                      </div>

                      {/* Recurring series details (optional) */}
                      {(() => {
                        const seriesId = (appointment as any)?.seriesId || (appointment as any)?.series_id;
                        if (!seriesId) return null;

                        const intervalWeeksRaw =
                          (appointment as any)?.seriesIntervalWeeks ??
                          (appointment as any)?.series_interval_weeks;
                        const occurrenceIndexRaw =
                          (appointment as any)?.occurrenceIndex ??
                          (appointment as any)?.occurrence_index;
                        const occurrenceCountRaw =
                          (appointment as any)?.seriesOccurrenceCount ??
                          (appointment as any)?.series_occurrence_count;

                        const intervalWeeks = Number(intervalWeeksRaw);
                        const occurrenceIndex = Number(occurrenceIndexRaw);
                        const occurrenceCount = Number(occurrenceCountRaw);

                        const everyLabel =
                          Number.isFinite(intervalWeeks) && intervalWeeks > 0
                            ? `Every ${intervalWeeks} ${intervalWeeks === 1 ? 'week' : 'weeks'}`
                            : null;

                        const indexLabel =
                          Number.isFinite(occurrenceIndex) &&
                          occurrenceIndex > 0 &&
                          Number.isFinite(occurrenceCount) &&
                          occurrenceCount > 0
                            ? `${occurrenceIndex} of ${occurrenceCount} in series`
                            : null;

                        const nextLabel = (() => {
                          const raw = (appointment as any)?.nextInSeriesScheduledAt;
                          if (!raw) return null;
                          const d = new Date(raw);
                          if (Number.isNaN(d.getTime())) return null;
                          const datePart = d.toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          });
                          const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                          return `Next: ${datePart} • ${timePart}`;
                        })();

                        const pillClass =
                          'inline-flex items-center gap-1 px-3 py-1 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700';

                        return (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {everyLabel && (
                              <span className={pillClass}>
                                <Repeat className="w-3.5 h-3.5 text-gray-500" aria-hidden />
                                <span>{everyLabel}</span>
                              </span>
                            )}
                            {indexLabel && (
                              <span className={pillClass}>
                                <ListOrdered className="w-3.5 h-3.5 text-gray-500" aria-hidden />
                                <span>{indexLabel}</span>
                              </span>
                            )}
                            {nextLabel && (
                              <span className={pillClass}>
                                <Calendar className="w-3.5 h-3.5 text-gray-500" aria-hidden />
                                <span>{nextLabel}</span>
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  
                  {/* Services List */}
                  {appointment.assignments && appointment.assignments.length > 0 && (
                        <div className="mb-4 space-y-2">
                          {appointment.assignments.map((assignment, index) => {
                            const professionalName = assignment.professionalFirstName && assignment.professionalLastName
                              ? `${assignment.professionalFirstName} ${assignment.professionalLastName.charAt(0)}.`
                              : assignment.professionalFirstName || assignment.professionalLastName || 'Any Professional';
                            
                            const rawPrice = (assignment as any)?.price;
                            const servicePrice = typeof rawPrice === 'number'
                              ? rawPrice
                              : typeof rawPrice === 'string'
                                ? parseFloat(rawPrice) || 0
                                : 0;
                            const isUnknownPrice = isPendingQuote || rawPrice === null || rawPrice === undefined;
                            
                            return (
                              <div key={assignment.serviceId || index} className="flex items-start gap-2 py-1.5">
                                <div className="flex-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm font-medium text-gray-900">
                                      {assignment.serviceName}
                                    </p>
                                    <p className="text-sm font-semibold text-gray-900">
                                      {isUnknownPrice ? (
                                        <span
                                          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700"
                                          title="Quotable price"
                                        >
                                          <HelpCircle className="w-4 h-4" />
                                        </span>
                                      ) : (
                                        <>${servicePrice.toFixed(2)}</>
                                      )}
                                    </p>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                                    Professional: {professionalName}
                                    {(() => {
                                      const rawLoc = String((assignment as any)?.serviceLocation || '').toUpperCase().trim();
                                      const isOnSite = rawLoc === 'ON_SITE' || rawLoc === 'ONSITE';
                                      if (!isOnSite) return null;
                                      return (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 ml-1">
                                          On-Site
                                        </span>
                                      );
                                    })()}
                                    {assignment.insistOnProfessional && (
                                      <span className="inline-flex items-center gap-0.5 text-red-500" title="Customer insists on this professional">
                                        <Heart className="w-3 h-3 fill-current" />
                                        <span className="text-xs">Requested</span>
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Estimated Total */}
                      {appointment.assignments && appointment.assignments.length > 0 && (
                        <div className="mb-4 pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-700">Estimated Total</span>
                            <span className="text-lg font-bold text-gray-900 flex items-center gap-1">
                              {isPendingQuote ? (
                                <span
                                  className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-700"
                                  title="Total pending quote"
                                >
                                  <HelpCircle className="w-5 h-5" />
                                </span>
                              ) : appointment.quoteId && appointment.quoteFinalAgreedAmount != null ? (
                                <>
                                  ${Number(appointment.quoteFinalAgreedAmount).toFixed(2)}
                                  <span className="text-red-500">*</span>
                                </>
                              ) : (
                                <>
                                  ${totalPrice.toFixed(2)}
                                  <span className="text-red-500">*</span>
                                </>
                              )}
                            </span>
                          </div>
                          <div className="flex items-start gap-2 mt-2">
                            <Info className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-gray-600">Prices are estimates and may change based on the final services provided.</p>
                          </div>
                          {appointment.receivedDepositAmount != null && Number(appointment.receivedDepositAmount) > 0 && (
                            <div className="flex flex-col gap-0.5 mt-2 pt-2 border-t border-gray-100">
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-600">Deposit paid</span>
                                <span className="font-medium text-gray-900">${Number(appointment.receivedDepositAmount).toFixed(2)}</span>
                              </div>
                              {appointment.depositPaidAt && (
                                <div className="text-xs text-gray-500">
                                  {formatDate(appointment.depositPaidAt)} at {formatTime(appointment.depositPaidAt)}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="pt-4 border-t border-gray-200">
                        {/* Mobile: Duration first */}
                        <div className="mb-4 md:hidden">
                          <div className="flex items-start gap-2">
                            <Clock className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">Duration</p>
                              <p className="text-sm text-gray-900 font-medium">{totalDuration} minutes</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Mobile: Business second */}
                        <div className="flex items-start gap-2 mb-4 md:hidden">
                          <Building2 className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-0.5">Business</p>
                            <div className="flex items-center gap-2">
                              {appointment.business_short_name ? (
                                <Link
                                  to={`/${appointment.business_short_name}`}
                                  className="text-sm font-medium hover:underline transition-colors"
                                  style={{ color: '#0B4778' }}
                                >
                                  {appointment.business_name}
                                </Link>
                              ) : (
                                <p className="text-sm text-gray-900 font-medium">{appointment.business_name}</p>
                              )}
                              <button
                                onClick={handleGetDirections}
                                className="relative group flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 hover:bg-blue-100 transition-colors"
                                title="Get directions"
                              >
                                <Navigation className="w-3.5 h-3.5 text-gray-600 group-hover:text-blue-600 transition-colors" />
                                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                                  Get directions
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Desktop: 3-column grid with Address/Phone/Email in same columns */}
                        <div className="hidden md:grid grid-cols-3 gap-0">
                          <div className="flex flex-col gap-0">
                            <div className="flex items-start gap-2">
                              <Building2 className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-xs text-gray-500 mb-0.5">Business</p>
                                <div className="flex items-center gap-2">
                                  {appointment.business_short_name ? (
                                    <Link
                                      to={`/${appointment.business_short_name}`}
                                      className="text-sm font-medium hover:underline transition-colors"
                                      style={{ color: '#0B4778' }}
                                    >
                                      {appointment.business_name}
                                    </Link>
                                  ) : (
                                    <p className="text-sm text-gray-900 font-medium">{appointment.business_name}</p>
                                  )}
                                  <button
                                    onClick={handleGetDirections}
                                    className="relative group flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 hover:bg-blue-100 transition-colors"
                                    title="Get directions"
                                  >
                                    <Navigation className="w-3.5 h-3.5 text-gray-600 group-hover:text-blue-600 transition-colors" />
                                    <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                                      Get directions
                                    </span>
                                  </button>
                                </div>
                              </div>
                            </div>
                            {businessInfo?.address && (
                              <div className="flex items-start gap-2 mt-4">
                                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-xs text-gray-500 mb-0.5">Address</p>
                                  <p className="text-sm text-gray-900 font-medium">{businessInfo.address}</p>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-0">
                            <div className="flex items-start gap-2">
                              <Clock className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs text-gray-500 mb-0.5">Duration</p>
                                <p className="text-sm text-gray-900 font-medium">{totalDuration} minutes</p>
                              </div>
                            </div>
                            {(businessInfo?.phone || businessInfo?.email) && (
                              <div className="grid grid-cols-2 gap-4 mt-4">
                                {businessInfo?.phone && (
                                  <div className="flex items-start gap-2">
                                    <Phone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <p className="text-xs text-gray-500 mb-0.5">Phone</p>
                                      <a href={`tel:${businessInfo.phone}`} className="text-sm font-medium" style={{ color: '#0B4778' }} onMouseEnter={(e) => e.currentTarget.style.color = '#0a3d66'} onMouseLeave={(e) => e.currentTarget.style.color = '#0B4778'}>
                                        {businessInfo.phone}
                                      </a>
                                    </div>
                                  </div>
                                )}
                                {businessInfo?.email && (
                                  <div className="flex items-start gap-2">
                                    <Mail className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <p className="text-xs text-gray-500 mb-0.5">Email</p>
                                      <a href={`mailto:${businessInfo.email}`} className="text-sm font-medium" style={{ color: '#0B4778' }} onMouseEnter={(e) => e.currentTarget.style.color = '#0a3d66'} onMouseLeave={(e) => e.currentTarget.style.color = '#0B4778'}>
                                        {businessInfo.email}
                                      </a>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-0">
                            {(() => {
                              const rawLocations = (appointment.assignments || [])
                                .map((a: any) => String(a?.serviceLocation || '').toUpperCase().trim())
                                .filter((x: string) => !!x);
                              const hasOnSite = rawLocations.some((l: string) => l === 'ON_SITE' || l === 'ONSITE');
                              const hasInStore = rawLocations.some((l: string) => l === 'IN_STORE' || l === 'INSTORE' || l === 'IN-STORE');

                              if (!hasOnSite && !hasInStore) return null;
                              if (!hasOnSite) return null; // only show when On-site or Mixed

                              const label =
                                hasOnSite && hasInStore ? 'Mixed (In-store + On-site)'
                                : 'On-site';

                              const addr = (appointment.assignments || []).find((a: any) => a?.serviceAddress)?.serviceAddress || null;
                              const hasAddr =
                                !!addr &&
                                typeof addr.address === 'string' && addr.address.trim() &&
                                typeof addr.city === 'string' && addr.city.trim() &&
                                typeof addr.state === 'string' && addr.state.trim() &&
                                typeof addr.zip === 'string' && addr.zip.trim();

                              return (
                                <div className="flex items-start gap-2">
                                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-500 mb-0.5">Service Location</p>
                                    <p className="text-sm text-gray-900 font-medium">{label}</p>
                                    {hasAddr && (
                                      <div className="mt-2 text-sm text-gray-700">
                                        <div className="font-medium">{addr.address}</div>
                                        <div>
                                          {addr.city}, {addr.state} {addr.zip}
                                        </div>
                                        {addr.locationNotes && String(addr.locationNotes).trim() !== '' && (
                                          <div className="mt-2 text-gray-600">
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Location Notes</span>
                                            <div className="mt-1">{addr.locationNotes}</div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        
                        {/* Mobile: Address, Phone, Email - shown on all screen sizes */}
                        <div className="grid grid-cols-1 md:hidden gap-4 mt-4">
                        
                        {businessInfo?.address && (
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">Address</p>
                              <p className="text-sm text-gray-900 font-medium">{businessInfo.address}</p>
                            </div>
                          </div>
                        )}
                        
                        {businessInfo?.phone && (
                          <div className="flex items-start gap-2">
                            <Phone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">Phone</p>
                              <a href={`tel:${businessInfo.phone}`} className="text-sm font-medium" style={{ color: '#0B4778' }} onMouseEnter={(e) => e.currentTarget.style.color = '#0a3d66'} onMouseLeave={(e) => e.currentTarget.style.color = '#0B4778'}>
                                {businessInfo.phone}
                              </a>
                            </div>
                          </div>
                        )}
                        
                        {businessInfo?.email && (
                          <div className="flex items-start gap-2">
                            <Mail className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">Email</p>
                              <a href={`mailto:${businessInfo.email}`} className="text-sm font-medium" style={{ color: '#0B4778' }} onMouseEnter={(e) => e.currentTarget.style.color = '#0a3d66'} onMouseLeave={(e) => e.currentTarget.style.color = '#0B4778'}>
                                {businessInfo.email}
                              </a>
                            </div>
                          </div>
                        )}

                        {/* Service Location (mobile) - only show when On-site or Mixed */}
                        {(() => {
                          const rawLocations = (appointment.assignments || [])
                            .map((a: any) => String(a?.serviceLocation || '').toUpperCase().trim())
                            .filter((x: string) => !!x);
                          const hasOnSite = rawLocations.some((l: string) => l === 'ON_SITE' || l === 'ONSITE');
                          const hasInStore = rawLocations.some((l: string) => l === 'IN_STORE' || l === 'INSTORE' || l === 'IN-STORE');

                          if (!hasOnSite) return null;

                          const label =
                            hasOnSite && hasInStore ? 'Mixed (In-store + On-site)'
                            : 'On-site';

                          const addr = (appointment.assignments || []).find((a: any) => a?.serviceAddress)?.serviceAddress || null;
                          const hasAddr =
                            !!addr &&
                            typeof addr.address === 'string' && addr.address.trim() &&
                            typeof addr.city === 'string' && addr.city.trim() &&
                            typeof addr.state === 'string' && addr.state.trim() &&
                            typeof addr.zip === 'string' && addr.zip.trim();

                          return (
                            <div className="flex items-start gap-2">
                              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs text-gray-500 mb-0.5">Service Location</p>
                                <p className="text-sm text-gray-900 font-medium">{label}</p>
                                {hasAddr && (
                                  <div className="mt-2 text-sm text-gray-700">
                                    <div className="font-medium">{addr.address}</div>
                                    <div>
                                      {addr.city}, {addr.state} {addr.zip}
                                    </div>
                                    {addr.locationNotes && String(addr.locationNotes).trim() !== '' && (
                                      <div className="mt-2 text-gray-600">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Location Notes</span>
                                        <div className="mt-1">{addr.locationNotes}</div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                        </div>
                      </div>
                  
                  {appointment.notes && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-md border border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Notes</p>
                      <p className="text-sm text-gray-900">{appointment.notes}</p>
                    </div>
                  )}

                  {showCustomerAppointmentMessage && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-md border border-blue-200">
                      <div className="flex items-start gap-2">
                        <MessageSquare className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-blue-800 font-semibold mb-1">
                            Message from {appointment.business_name}
                          </p>
                          <p className="text-sm text-blue-900 whitespace-pre-line break-words">
                            {customerAppointmentMessage?.message?.trim()}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  
                  {/* Rating section for completed appointments */}
                  {appointment.status === 'COMPLETED' && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-900">Service Ratings</h4>
                        {hasAnyServicesReviewed() && (
                          <button
                            onClick={handleRateAppointment}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-900 hover:text-blue-800 hover:underline focus:outline-none"
                          >
                            See my reviews
                          </button>
                        )}
                      </div>
                      {reviewsLoading ? (
                        <div className="text-sm text-gray-600 italic">Loading ratings...</div>
                      ) : (
                        <div className="space-y-2">
                          {appointment.assignments?.map((assignment) => {
                            const review = appointmentReviews.find((r: any) => r.serviceId === assignment.serviceId);
                            
                            return (
                              <div key={assignment.serviceId} className="flex items-center justify-between py-1">
                                <span className="text-sm text-gray-900">
                                  {assignment.serviceName}
                                </span>
                                <div className="flex items-center gap-2">
                                  {review ? (
                                    <div className="flex items-center gap-1">
                                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                                      <span className="text-sm text-gray-900 font-medium">({review.rating}/5)</span>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-gray-500 italic">Not rated</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  
                  {/* Action buttons at bottom of card */}
                  <div className="flex flex-col sm:flex-row gap-2 mt-5 pt-5 border-t border-gray-200 relative overflow-visible">
                    {/* View Button - Already on details page, so we can skip this */}
                    
                    {/* Manage Dropdown */}
                    {(() => {
                      const isPast = isAppointmentInPast();
                      if (isPast) return null;

                      const canReschedule = appointment.status === 'CONFIRMED' || appointment.status === 'PENDING';
                      const canCancel = appointment.status === 'CONFIRMED' || appointment.status === 'PENDING';
                      const showDeposit = seriesDepositDue;
                      const hasActions = canReschedule || canCancel || showDeposit;

                      if (!hasActions) return null;

                      return (
                      <div className="relative w-full sm:w-auto" ref={manageDropdownRef}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsManageDropdownOpen(prev => !prev);
                          }}
                          className="aacore-btn-outlined w-full sm:w-auto justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Settings className="w-4 h-4" />
                          Manage <ChevronDown className={`w-4 h-4 transition-transform ${isManageDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isManageDropdownOpen && (
                          <div 
                            className="absolute mt-1 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-[100] overflow-hidden" 
                            style={{ 
                              minWidth: '224px',
                              left: 0,
                              right: 'auto'
                            }}
                          >
                            {showDeposit && (
                              <Link
                                to={`/dashboard/customer/appointments/${appointment.id}/deposit`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsManageDropdownOpen(false);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer border-b border-gray-100"
                              >
                                <DollarSign className="w-4 h-4 mr-2" />
                                Deposit
                              </Link>
                            )}
                            {canReschedule && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsManageDropdownOpen(false);
                                  handleRescheduleAppointment();
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer border-b border-gray-100"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Reschedule Appointment
                              </button>
                            )}
                            {canCancel && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsManageDropdownOpen(false);
                                  handleCancelAppointment();
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center cursor-pointer"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Cancel Appointment
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })()}

                    {(() => {
                      const canRebook =
                        appointment.status === 'COMPLETED' ||
                        (isAppointmentInPast() && appointment.status !== 'CANCELLED');
                      if (!canRebook) return null;

                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRebookAppointment();
                          }}
                          className="aacore-btn2-primary rounded-full flex-1 sm:flex-none"
                        >
                          <CalendarPlus className="w-4 h-4" />
                          Rebook
                        </button>
                      );
                    })()}
                    
                    {(() => {
                      const canLeaveReview =
                        isAppointmentInPast() &&
                        appointment.status !== 'CANCELLED' &&
                        !hasAnyReviewsOrRatings();

                      if (!canLeaveReview) return null;

                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRateAppointment();
                          }}
                          className="aacore-btn2-success rounded-full flex-1 sm:flex-none"
                        >
                          <Star className="w-4 h-4" />
                          Leave Reviews
                        </button>
                      );
                    })()}

                    {!isAppointmentInPast() &&
                      appointment.status !== 'CANCELLED' &&
                      appointment.status !== 'COMPLETED' && (
                      <>
                        {/* Add to Calendar Dropdown */}
                        <div className="relative w-full sm:w-auto" ref={calendarDropdownRef}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsCalendarDropdownOpen(prev => !prev);
                            }}
                            className="aacore-btn-outlined w-full sm:w-auto justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <CalendarPlus className="w-4 h-4" />
                            Add to Calendar
                            <ChevronDown className={`w-4 h-4 transition-transform ${isCalendarDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {isCalendarDropdownOpen && (
                            <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50 overflow-hidden">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsCalendarDropdownOpen(false);
                                  window.open(getGoogleCalendarUrl(), '_blank');
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer border-b border-gray-100"
                              >
                                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                </svg>
                                Google Calendar
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsCalendarDropdownOpen(false);
                                  window.open(getOutlookCalendarUrl(), '_blank');
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer"
                              >
                                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M7.5 21h9a1.5 1.5 0 0 0 1.5-1.5V9H6v10.5A1.5 1.5 0 0 0 7.5 21zM6 7.5V4.5A1.5 1.5 0 0 1 7.5 3h9A1.5 1.5 0 0 1 18 4.5V7.5H6z" fill="#0078D4"/>
                                  <path d="M12 12.75c-1.24 0-2.25-1.01-2.25-2.25S10.76 8.25 12 8.25s2.25 1.01 2.25 2.25S13.24 12.75 12 12.75z" fill="#0078D4"/>
                                </svg>
                                Outlook
                              </button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancellation Modal */}
      {cancellationAppointment && (
        <CustomerCancellationModal
          isOpen={isCancellationModalOpen}
          onClose={handleCancellationCancelled}
          onConfirm={handleCancellationConfirmed}
          appointmentDetails={(() => {
            const iso = getAppointmentStartIso(cancellationAppointment);
            const when = iso ? `${new Date(iso).toLocaleDateString()} at ${new Date(iso).toLocaleTimeString()}` : 'Date TBD';
            return `${cancellationAppointment.assignments?.map(assignment => assignment.serviceName).join(', ') || 'Appointment'} at ${cancellationAppointment.business_name} on ${when}`;
          })()}
          appointment={cancellationAppointment as any}
          isLoading={isCancelling}
        />
      )}

      {/* Review Modal */}
      {appointment && (
        <ServiceReviewModal
          appointment={appointment}
          isOpen={isReviewModalOpen}
          onClose={handleReviewModalClose}
          onReviewSubmitted={handleReviewSubmitted}
        />
      )}

      <Footer />

      <RebookConfirmModal
        isOpen={isRebookModalOpen}
        appointment={appointment}
        onClose={() => setIsRebookModalOpen(false)}
        onEditServices={handleRebookEditServices}
        onContinue={handleRebookContinue}
      />
    </div>
  );
};

export default CustomerAppointmentDetailsPage;

